import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ChallengeForm, { TYPE_LABEL } from "@/pages/admin/ChallengeForm";
import ChallengeDetail, { StatusPill } from "@/pages/admin/ChallengeDetail";

const FILTERS = [["", "All"], ["draft", "Draft"], ["active", "Active"], ["paused", "Paused"], ["ended", "History"]];
const d = (iso) => (iso ? new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" }) : "—");

export default function ChallengesTab({ me }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (st = filter) => {
    try {
      const { data } = await api.get("/admin/challenges", { params: { status: st || undefined } });
      setRows(data.challenges);
    } catch { toast.error("Couldn't load challenges."); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const submit = async (payload) => {
    setBusy(true);
    try {
      if (editing?.id) {
        await api.patch(`/admin/challenges/${editing.id}`, payload);
        toast.success("Challenge updated.");
      } else {
        const { data } = await api.post("/admin/challenges", payload);
        toast.success("Challenge created as draft — press Start when ready.");
        setSelected(data.id);
      }
      setFormOpen(false); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/challenges/${c.id}`); toast.success("Challenge deleted."); if (selected === c.id) setSelected(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  return (
    <div data-testid="admin-challenges-tab" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(([v, label]) => (
          <button key={v} data-testid={`challenges-filter-${v || "all"}`} onClick={() => { setFilter(v); }}
            className={`text-xs uppercase tracking-widest px-3 py-2 border ${filter === v ? "border-rose-600 text-rose-400" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`}>{label}</button>
        ))}
        <Button data-testid="challenge-create-btn" onClick={() => { setEditing(null); setFormOpen(true); }} className="btn-crimson rounded-none ml-auto"><Plus size={14} className="mr-1" /> New challenge</Button>
      </div>

      <div className="space-y-4">
        <div className="card-surface grid sm:grid-cols-2 xl:grid-cols-3 gap-px bg-white/5 max-h-[320px] overflow-y-auto" data-testid="challenges-list">
          {rows.map((c) => (
            <div key={c.id} data-testid={`challenge-row-${c.id}`} onClick={() => setSelected(c.id)} role="button" tabIndex={0}
              className={`p-3 cursor-pointer bg-[#0d0d0d] hover:bg-white/5 ${selected === c.id ? "bg-rose-500/10 border-l-2 border-rose-500" : "border-l-2 border-transparent"}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-sm truncate">{c.title}</div><StatusPill s={c.status} />
              </div>
              <div className="text-[11px] text-zinc-500 mt-0.5">{TYPE_LABEL[c.type].split(" — ")[0]} · {d(c.start_at)} → {d(c.end_at)}</div>
              {(c.status === "draft" || me?.is_owner) && (
                <button data-testid={`challenge-delete-${c.id}`} onClick={(e) => { e.stopPropagation(); remove(c); }} className={`mt-1 text-[10px] inline-flex items-center gap-1 ${c.status === "draft" ? "text-zinc-600 hover:text-rose-400" : "text-amber-500/70 hover:text-amber-300"}`}><Trash2 size={10} /> {c.status === "draft" ? "Delete" : "Delete · Owner override"}</button>
              )}
            </div>
          ))}
          {rows.length === 0 && <div className="p-6 text-center text-xs text-zinc-600 bg-[#0d0d0d] sm:col-span-2 xl:col-span-3">No challenges {filter ? `in ${filter}` : "yet"}. Create one to get started.</div>}
        </div>
        <div className="min-w-0">
          {selected ? <ChallengeDetail id={selected} me={me} onEdit={(c) => { setEditing(c); setFormOpen(true); }} onChanged={load} />
            : <div className="card-surface p-8 text-center text-sm text-zinc-500" data-testid="challenge-empty-state">Select a challenge to see rankings, progress and controls.</div>}
        </div>
      </div>

      <ChallengeForm open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) setEditing(null); }} initial={editing} onSubmit={submit} busy={busy} />
    </div>
  );
}
